'use client'
import React, { useState, useEffect } from "react";
import { TextField, Card, CardContent, CardHeader, FormControl, InputLabel, Input, InputAdornment, IconButton, MenuItem, FormHelperText} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Select from '@mui/material/Select';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { GCInfoErrors, GCInfoModel } from "@/app/Models/GCInfoModel";
import { CardSiteDataModel } from "@/app/Models/CardSiteDataModel";

interface Props {
    data: GCInfoModel,
    setData: (model: GCInfoModel) => void;
    cardSiteData: CardSiteDataModel | null;
    errors: GCInfoErrors;
}

const GCInfoCard: React.FC<Props> = (props) => {
    const [showGCNumber, setShowGCNumber] = useState(false);
    const [showGCPin, setShowGCPIn] = useState(false);
    const [issuerName, setIssuerName] = useState("");

    const handleClickShowPassword = (control: String) => 
    { 
        if(control === 'number') setShowGCNumber((show) => !show);
        else if (control === 'pin') setShowGCPIn((show) => !show);
    }

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    useEffect(() => {
        if (props.cardSiteData?.issuer) {
            setIssuerName(props.cardSiteData.issuer);
            props.setData({...props.data, issuerName: props.cardSiteData.issuer});
        }
    }, [props.cardSiteData]);

    const processorCode: string = props.cardSiteData?.processorCode ?? "";
    const brands: readonly string[] = props.cardSiteData?.brandNames ?? [];

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Card sx={{ minWidth: 275}} variant="outlined">
                <CardHeader title='Gift Card Information' 
                    avatar={
                        <CreditCardIcon />
                    }/>
                <CardContent>                                
                    <FormControl sx={{ m: 1, width: '40ch'}} variant="standard">
                        <InputLabel htmlFor="gc-number-input">Gift Card Number</InputLabel>
                        <Input id="gc-number-input"
                            value={props.data.giftCardNumber}
                            onChange={(e) => props.setData({...props.data, giftCardNumber: e.target.value})}
                            error={props.errors.giftCardNumberError}
                            type={showGCNumber ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPassword('number')}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end">
                                        {showGCNumber ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            } />
                    </FormControl>

                    <FormControl sx={{ m: 1, width: '40ch'}} variant="standard">
                        <InputLabel htmlFor="gc-pin-input">Gift Card Pin</InputLabel>
                        <Input id="gc-pin-input"
                            value={props.data.giftCardPin}
                            onChange={(e) => props.setData({...props.data, giftCardPin: e.target.value})}
                            error={props.errors.giftCardPinError}
                            type={showGCPin ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPassword('pin')}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end">
                                        {showGCPin ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            } />
                    </FormControl>

                    <TextField
                        required
                        variant="standard"
                        value={issuerName}
                        error={props.errors.issuerNameError}
                        id="gc-issuer-textfield"
                        helperText="The name of the company that issued the gift card. This should be located on the back of the card."
                        label="Issuer Name" />
                    
                    <FormControl sx={{ m: 1, width: '40ch' }}>
                        <InputLabel htmlFor="gc-brand-input">Brand Name</InputLabel>
                        <Select 
                            labelId="gc-brand-select-label"
                            id="gc-brand-select"
                            value={props.data.brandName || ""}
                            label="Brand Name"
                            onChange={(e) => props.setData({...props.data, brandName: e.target.value})}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {brands.map((brand) => (
                                    <MenuItem key={brand} value={brand}>
                                        {brand}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>Select the brand (or one brand if multiple) that the gift card is for.</FormHelperText>
                    </FormControl>

                    <TextField
                        required
                        type="number"
                        variant="standard"
                        value={props.data.cashOutAmount}
                        error={props.errors.cashOutAmountError}
                        onChange={(e) => {
                            const value = e.target.value;
                            
                            if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
                                props.setData({...props.data, cashOutAmount: value || "0.00"});
                            }
                        }}
                        id="gc-amount-textfield"
                        helperText="Enter the remaining balance on the gift card."
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputMode: "decimal",
                            style: {
                                appearance: 'textfield',
                                MozAppearance: 'textfield',
                            }
                        }}
                        sx={{
                            '& input[type=number]::-webkit-outer-spin-button': {
                                display: 'none',
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                                display: 'none',
                            },
                        }}
                        label="Remaining Balance" />

                    {processorCode === "Manual" &&
                    <DatePicker
                        value={props.data.cardPurchasedDate}
                        onChange={(date) => props.setData({...props.data, cardPurchasedDate: date})}
                        label="Card Purchase Date"
                    />
                    }

                    {processorCode === "Manual" &&
                    <TextField
                        required
                        type="number"
                        variant="standard"
                        value={props.data.beginningCardBalance}
                        error={props.errors.beginningCardBalanceError}
                        onChange={(e) => {
                            const value = e.target.value;
                            
                            if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
                                props.setData({...props.data, beginningCardBalance: value || "0.00"});
                            }
                        }}
                        id="gc-beginning-card-balance"
                        helperText="Enter the initial amount loaded to the gift card."
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputMode: "decimal",
                            style: {
                                appearance: 'textfield',
                                MozAppearance: 'textfield',
                            }
                        }}
                        sx={{
                            '& input[type=number]::-webkit-outer-spin-button': {
                                display: 'none',
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                                display: 'none',
                            },
                        }}
                        label="Beginning Card Balance" />
                    }
                </CardContent>
            </Card>
        </LocalizationProvider>
        
    );
};

export default GCInfoCard;